import { useCallback, useEffect } from "react";

import { useUpdateCheckStore } from "~/stores/useUpdateCheckStore.ts";
import { reloadApp } from "~/utils/appCacheUtils.ts";

const UPDATE_INTERVAL = 60 * 1000;

// Matches the Tailwind breakpoint `lg`. Below it, the app has no nav items, which do the reload on a desktop.
const LG_MEDIA_QUERY = "(min-width: 1024px)";

function UpdateChecker() {
	const {
		setUpdateAvailable,
	} = useUpdateCheckStore((state) => state);

	const checkForUpdates = useCallback(async () => {
		try {
			// Fetch the latest manifest
			const manifestResponse = await fetch("/manifest.json", {
				cache: "no-store",
				headers: { "Cache-Control": "no-cache" },
			});

			if (!manifestResponse.ok) {
				console.warn("Manifest file not accessible. Skipping update check.");
				return;
			}

			const manifest = await manifestResponse.json();
			let latestJsFile = manifest["index.html"]?.file;

			if (!latestJsFile) {
				console.warn("Unable to find main JavaScript file in manifest.");
				return;
			}
			latestJsFile = latestJsFile.replace("assets/", "");

			// Get the current script filename from the DOM
			const scripts = document.getElementsByTagName("script");
			const currentScript = Array.from(scripts).find(script =>
				script.src.includes("assets/index-") && script.src.endsWith(".js"),
			);

			if (!currentScript) {
				console.warn("Unable to find current script in the DOM.");
				return;
			}

			const currentJsFile = currentScript.src.split("/").pop();

			// Compare the filenames
			if (currentJsFile !== latestJsFile) {
				setUpdateAvailable(true);
			}

		} catch (error) {
			console.error("Error checking for updates:", error);
		}
	}, [setUpdateAvailable]);

	useEffect(() => {
		if(process.env.NODE_ENV === "development") return;
		checkForUpdates(); // Check immediately when component mounts

		const intervalId = setInterval(checkForUpdates, UPDATE_INTERVAL);

		/**
		 * Below `lg`, the update loads when the user comes back to the page: a phone user switches apps often, and the
		 * reload then interrupts no work. An open dialog can hold unsaved input, so the reload waits for the next return.
		 */
		const reloadOnReturn = async () => {
			if (document.visibilityState !== "visible" || window.matchMedia(LG_MEDIA_QUERY).matches) return;
			await checkForUpdates();
			if (useUpdateCheckStore.getState().updateAvailable && !document.querySelector("[role=\"dialog\"]")) {
				reloadApp();
			}
		};
		document.addEventListener("visibilitychange", reloadOnReturn);

		return () => {
			clearInterval(intervalId);
			document.removeEventListener("visibilitychange", reloadOnReturn);
		};
	}, [checkForUpdates]);

/*	if (updateAvailable) {
		return (
			<div className="fixed bottom-10 right-10 flex items-center gap-x-2 rounded-lg bg-white px-4 py-3 text-sm shadow-lg">
				Ein App-Update ist verfügbar. Bitte lade die Seite neu.
				<Button onClick={reloadApp}>
					Jetzt neu laden
				</Button>
			</div>
		);
	}*/

	return null;
}

export default UpdateChecker;